import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../components/AuthContext';
import { useToast } from '../../../components/ToastContext';
import { useOrg } from '../../../components/OrgContext';
import { useBranding } from '../../../components/BrandingContext';
import { logAudit } from '../../../hooks/useAudit';
import { uploadSiteLogo, clearSiteLogo } from '../../../lib/siteLogo';
import { SITE_MODULES } from '../../../lib/modules';
import PageBar from '../../../components/PageBar';
import { FullCopyPane, RetentionPane } from './DataPanes';
import NotBuiltYet from '../../../components/NotBuiltYet';
import PageLoading from '../../../components/PageLoading';

// Settings -> Site Settings.
//
// Everything about the site itself, as opposed to the data in it. Categories
// on the left, the chosen category's form on the right.
//
// The site being edited is always the site you are working in. A Global Admin
// reaches another site's settings by switching into it, the same way they
// reach its counts - so nothing here reads or writes across the isolation
// line, and nothing needs a way around it. That is why the rail lists
// categories rather than sites: the answer to "which site" is already on the
// screen, in the header.
//
// Which categories a person sees does not change; what changes is whether the
// form is editable. A Site Admin edits their own site. Only a Global Admin
// moves is_active, because my_orgs() hides an inactive site and switching your
// own site off would lock you out of the screen that switches it back on. The
// database enforces both - this screen only reflects them.

const TIME_ZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
  'America/Puerto_Rico', 'UTC',
];

const RAIL_GROUPS = [
  { label: 'The site', items: [
    { key: 'general',  label: 'General' },
    { key: 'address',  label: 'Address' },
    { key: 'branding', label: 'Logo' },
  ] },
  { label: 'How it works', items: [
    { key: 'modules',  label: 'Modules' },
    { key: 'counting', label: 'Counting rules' },
  ] },
  { label: 'What people see', items: [
    { key: 'release_notes', label: 'Release notes' },
    { key: 'terms',         label: 'Terms & conditions' },
  ] },
  /*
    **Data joined the rail on 7 September 2026, job 17.** The owner asked for
    three things about data and all three land here: taking everything out,
    keeping that copy fresh, and what happens to a record after three years.
    They are categories on an approved screen rather than a new screen, because
    every screen in OnHand was drawn and signed off by 5 September and a new one
    would be a screen nobody drew.
  */
  { label: 'Data', items: [
    { key: 'full_copy', label: 'Take everything out' },
    { key: 'retention', label: 'How long things are kept' },
  ] },
  { label: 'Connections', items: [
    { key: 'integrations',  label: 'API & integrations', soon: true },
    { key: 'transfers',     label: 'FTP & SFTP',         soon: true },
    { key: 'notifications', label: 'Notifications',      soon: true },
  ] },
];

const PANE_TITLES = {
  general:       ['General', 'What this site is called, where it sits in time, and whether it is switched on.'],
  address:       ['Address', 'The client this site belongs to. Reports and exports read these details.'],
  branding:      ['Logo', 'The logo this site shows its own people. Not the OnHand mark.'],
  modules:       ['Modules', 'Which parts of the app this site can see.'],
  counting:      ['Counting rules', "The numbers this site's count alerts are measured against."],
  release_notes: ['Release notes', 'What changed. Each person is shown the newest once, the first time they sign in after it is published.'],
  terms:         ['Terms & conditions', 'What people agree to. Published terms are shown until they are accepted.'],
  full_copy:     ['Take everything out', 'One file holding every table in this site, so a copy of it exists somewhere you control.'],
  retention:     ['How long things are kept', 'What ages out of this site, what never does, and what is over the line right now.'],
  integrations:  ['API & integrations', 'Connections to the systems this site already runs on.'],
  transfers:     ['FTP & SFTP', 'Scheduled file drops in and out of this site.'],
  notifications: ['Notifications', 'Who hears about what, and how.'],
};

function Field({ label, children, hint, span }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, gridColumn: span ? '1 / -1' : 'auto' }}>
      <span className="fld-label">{label}</span>
      {children}
      {hint && <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{hint}</span>}
    </label>
  );
}

// A category that has a name and a place in the rail but nothing behind it yet.
// The shape being visible is the point - it says where the work will land
// rather than pretending the category does not exist.
//
// **It is the app's one "not built yet" shape since 10 September 2026**, job 25.
// It used to be its own layout, one of two in OnHand for the same idea, and the
// three words across the app ("Soon", "Coming Soon", "Not built yet") are now
// one. Each of these three keeps its own sentence, which is a departure from the
// approved pane and is written up in `src/components/NotBuiltYet.jsx`.
function NotYet({ title, note }) {
  return <NotBuiltYet title={title} says={note} pane />;
}

export default function SiteSettings() {
  const { profile } = useAuth();
  const toast = useToast();
  const { current, reload: reloadOrgs } = useOrg();
  const { companyLogoUrl, refreshCompanyLogo } = useBranding();

  const [pane, setPaneRaw] = useState(() => sessionStorage.getItem('siteSettingsPane') || 'general');
  function setPane(p) { sessionStorage.setItem('siteSettingsPane', p); setPaneRaw(p); }

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [iAmGlobal, setIAmGlobal] = useState(false);

  const [site, setSite]       = useState(null);   // the organizations row, as edited
  const [rules, setRules]     = useState({ variance_alert_threshold_pct: '', not_started_alert_days_before_close: '' });
  const [notes, setNotes]     = useState([]);
  const [terms, setTerms]     = useState([]);

  const orgId = current?.id || null;

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const [allowed, global, org, settings, noteRows, termRows] = await Promise.all([
      supabase.rpc('can_manage_access', { target_org: null }),
      supabase.rpc('is_global_admin'),
      supabase.from('organizations').select('*').eq('id', orgId).maybeSingle(),
      supabase.from('app_settings').select('key, value'),
      supabase.from('release_notes').select('*').order('created_at', { ascending: false }),
      supabase.from('site_terms').select('*').order('created_at', { ascending: false }),
    ]);
    setCanManage(!!allowed.data);
    setIAmGlobal(!!global.data);
    setSite(org.data || null);
    const bag = {};
    (settings.data || []).forEach(r => { bag[r.key] = r.value; });
    setRules({
      variance_alert_threshold_pct: bag.variance_alert_threshold_pct ?? '',
      not_started_alert_days_before_close: bag.not_started_alert_days_before_close ?? '',
    });
    setNotes(noteRows.data || []);
    setTerms(termRows.data || []);
    setLoading(false);
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const readOnly = !canManage;

  function patch(fields) { setSite(s => ({ ...s, ...fields })); }

  // Name, address, time zone, clock and status all live on the same row, so
  // they save together whichever pane they were typed into.
  async function saveSite() {
    if (!site) return;
    setSaving(true);
    const body = {
      name: (site.name || '').trim(),
      address_line1: site.address_line1 || null,
      address_line2: site.address_line2 || null,
      city: site.city || null,
      state: site.state || null,
      postal_code: site.postal_code || null,
      country: site.country || null,
      phone: site.phone || null,
      time_zone: site.time_zone,
      clock_format: site.clock_format,
    };
    if (!body.name) { toast.error('A site needs a name.'); setSaving(false); return; }
    // is_active is deliberately absent. It is moved from Settings -> Sites, by
    // a Global Admin, and the database refuses it from anywhere else.

    const { error } = await supabase.from('organizations').update(body).eq('id', site.id);
    if (error) { toast.error('Could not save: ' + error.message); setSaving(false); return; }
    await logAudit(profile, 'SITE_SETTINGS_UPDATED', 'site', { target_name: body.name });
    toast.success('Saved');
    setSaving(false);
    await reloadOrgs();
    load();
  }

  // app_settings.value is not nullable, so a rule left blank is a row that
  // should not exist rather than a row holding an empty string. Clearing a box
  // has to mean "no rule", or a threshold could be typed away and still apply.
  // org_id defaults to current_org_id(), so the site stamps itself.
  async function saveRules() {
    setSaving(true);
    const entries = Object.entries(rules);
    const rows  = entries.filter(([, v]) => String(v).trim() !== '')
                         .map(([key, value]) => ({ key, value: String(value).trim() }));
    const clear = entries.filter(([, v]) => String(v).trim() === '').map(([key]) => key);

    if (rows.length) {
      const { error } = await supabase.from('app_settings').upsert(rows, { onConflict: 'org_id,key' });
      if (error) { toast.error('Could not save: ' + error.message); setSaving(false); return; }
    }
    if (clear.length) {
      const { error } = await supabase.from('app_settings').delete().in('key', clear);
      if (error) { toast.error('Could not save: ' + error.message); setSaving(false); return; }
    }
    toast.success('Saved');
    setSaving(false);
  }

  async function saveModules(next) {
    const { error } = await supabase.from('organizations').update({ modules: next }).eq('id', site.id);
    if (error) { toast.error('Could not save: ' + error.message); return; }
    patch({ modules: next });
    await reloadOrgs();
    toast.success('Saved');
  }

  if (!orgId) return <><PageBar title="Site Settings" /><div className="card" style={{ padding: 32 }}>Loading the site...</div></>;

  const [paneTitle, paneNote] = PANE_TITLES[pane] || ['', ''];

  return (
    <>
      <PageBar
        title="Site Settings"
        meta={current ? current.name : undefined}
      />

      <div className="ua-split">
        <div className="card ss-rail">
          {RAIL_GROUPS.map((group, gi) => (
            <div key={group.label} className={'ss-rail-group' + (gi === 0 ? ' first' : '')}>
              <div className="ss-rail-label">{group.label}</div>
              {group.items.map(item => (
                <button
                  key={item.key}
                  className={'ss-rail-item' + (pane === item.key ? ' on' : '')}
                  onClick={() => setPane(item.key)}
                >
                  <span>{item.label}</span>
                  {item.soon && <span className="ss-rail-soon">Soon</span>}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="card ua-detail">
          <div className="ua-detail-body">
            <div className="ua-detail-head">
              <div style={{ minWidth: 0 }}>
                <div className="ua-detail-name">{paneTitle}</div>
                <div className="ua-detail-sub">{paneNote}</div>
              </div>
            </div>

            {loading ? (
              <PageLoading />
            ) : (
              <>
                {readOnly && (
                  <div className="ss-readonly">
                    You can see this site's settings but not change them. Changing them needs
                    Site Admin here.
                  </div>
                )}

                {pane === 'general' && site && (
                  <GeneralPane
                    site={site} patch={patch} readOnly={readOnly} iAmGlobal={iAmGlobal}
                    saving={saving} onSave={saveSite}
                  />
                )}

                {pane === 'address' && site && (
                  <AddressPane site={site} patch={patch} readOnly={readOnly} saving={saving} onSave={saveSite} />
                )}

                {pane === 'branding' && (
                  <BrandingPane
                    orgId={orgId} readOnly={readOnly} toast={toast}
                    logoUrl={companyLogoUrl} refresh={refreshCompanyLogo}
                  />
                )}

                {pane === 'modules' && site && (
                  <ModulesPane modules={site.modules || {}} readOnly={readOnly} onChange={saveModules} />
                )}

                {pane === 'counting' && (
                  <CountingPane rules={rules} setRules={setRules} readOnly={readOnly} saving={saving} onSave={saveRules} />
                )}

                {pane === 'release_notes' && (
                  <NoticePane
                    kind="release_note" rows={notes} readOnly={readOnly}
                    profile={profile} toast={toast} onChanged={load}
                  />
                )}

                {pane === 'terms' && (
                  <NoticePane
                    kind="terms" rows={terms} readOnly={readOnly}
                    profile={profile} toast={toast} onChanged={load}
                  />
                )}

                {pane === 'full_copy' && (
                  <FullCopyPane
                    company={current?.name} readOnly={readOnly}
                    profile={profile} toast={toast}
                  />
                )}

                {pane === 'retention' && (
                  <RetentionPane readOnly={readOnly} toast={toast} />
                )}

                {pane === 'integrations' && (
                  <NotYet title="Nothing is connected yet"
                    note="This is where a site's own API keys and third-party connections will live - the Claimsoft push first, then whatever each site already runs on. Keys are held by the server, never by the browser." />
                )}
                {pane === 'transfers' && (
                  <NotYet title="No file transfers set up"
                    note="Scheduled FTP and SFTP drops in and out of this site: where to connect, which folder, how often, and what to do with what arrives." />
                )}
                {pane === 'notifications' && (
                  <NotYet title="Notifications are not wired up"
                    note="Who hears about what, and how. A submitted count emails its summary from OnHand's own address; until OnHand goes live it goes to a testing list rather than to the people it names." />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function GeneralPane({ site, patch, readOnly, iAmGlobal, saving, onSave }) {
  return (
    <>
      <div className="ua-fields">
        <Field label="Site name" hint="What this site is called everywhere in the app.">
          <input className="fld" value={site.name || ''} disabled={readOnly}
            onChange={e => patch({ name: e.target.value })} />
        </Field>
        <Field label="Web address" hint="Set when the site was created, and not changed after.">
          <input className="fld" value={site.slug || ''} disabled readOnly />
        </Field>
        <Field label="Time zone" hint="Times this site shows are read in this zone, not the browser's.">
          <select className="fld-sel" value={site.time_zone || 'America/Chicago'} disabled={readOnly}
            onChange={e => patch({ time_zone: e.target.value })}>
            {TIME_ZONES.map(z => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
          </select>
        </Field>
        <Field label="Clock" hint="How this site writes a time. It does not change how one is stored.">
          <select className="fld-sel" value={site.clock_format || '12h'} disabled={readOnly}
            onChange={e => patch({ clock_format: e.target.value })}>
            <option value="12h">12 hour (4:30 PM)</option>
            <option value="24h">24 hour (16:30)</option>
          </select>
        </Field>
      </div>

      <div className={'ua-ga-row' + (site.is_active ? '' : ' on')}>
        <div style={{ minWidth: 0 }}>
          <div className="ua-ga-title">{site.is_active ? 'This site is active' : 'This site is switched off'}</div>
          <div className="ua-ga-note">
            A switched-off site disappears from everybody's site list, including the
            list you would use to switch it back on. Only a Global Admin can move it, and
            not for the site they are standing in - that is done from Sites, after switching
            into another one.
          </div>
        </div>
        {iAmGlobal
          ? <span className="ua-rolepill">Change it under Settings, then Sites</span>
          : <span className="ua-rolepill">Global Admin only</span>}
      </div>

      {!readOnly && (
        <div className="ua-actions">
          <span style={{ flex: 1 }} />
          <button className="tb-btn tb-btn-primary" disabled={saving} onClick={() => onSave()}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      )}
    </>
  );
}

function AddressPane({ site, patch, readOnly, saving, onSave }) {
  return (
    <>
      <div className="ua-fields">
        <Field label="Address line 1" span>
          <input className="fld" value={site.address_line1 || ''} disabled={readOnly}
            onChange={e => patch({ address_line1: e.target.value })} />
        </Field>
        <Field label="Address line 2" span>
          <input className="fld" value={site.address_line2 || ''} disabled={readOnly}
            onChange={e => patch({ address_line2: e.target.value })} />
        </Field>
        <Field label="City">
          <input className="fld" value={site.city || ''} disabled={readOnly}
            onChange={e => patch({ city: e.target.value })} />
        </Field>
        <Field label="State">
          <input className="fld" value={site.state || ''} disabled={readOnly}
            onChange={e => patch({ state: e.target.value })} />
        </Field>
        <Field label="ZIP / postal code">
          <input className="fld" value={site.postal_code || ''} disabled={readOnly}
            onChange={e => patch({ postal_code: e.target.value })} />
        </Field>
        <Field label="Country">
          <input className="fld" value={site.country || ''} disabled={readOnly}
            onChange={e => patch({ country: e.target.value })} />
        </Field>
        <Field label="Phone">
          <input className="fld" value={site.phone || ''} disabled={readOnly}
            onChange={e => patch({ phone: e.target.value })} />
        </Field>
      </div>
      {!readOnly && (
        <div className="ua-actions">
          <span style={{ flex: 1 }} />
          <button className="tb-btn tb-btn-primary" disabled={saving} onClick={() => onSave()}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      )}
    </>
  );
}

function BrandingPane({ orgId, readOnly, toast, logoUrl, refresh }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await uploadSiteLogo(file, orgId);
      await refresh();
      toast.success('Logo uploaded.');
    } catch (err) {
      toast.error('Upload failed: ' + (err.message || 'Unknown error'));
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function onRemove() {
    setBusy(true);
    try { await clearSiteLogo(); await refresh(); toast.success('Logo removed.'); }
    catch { toast.error('Could not remove the logo.'); }
    setBusy(false);
  }

  return (
    <>
      <div className="ss-logo">
        <div className="ss-logo-frame">
          {logoUrl
            ? <img src={logoUrl} alt="" />
            : <span className="ss-logo-empty">No logo yet</span>}
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="ua-ga-title">This site's logo</div>
          <div className="ua-ga-note">
            PNG, JPG, SVG or WebP, up to 2 MB. It sits beside the OnHand mark in the header
            rather than replacing it. The stored file is kept when you remove it, so putting
            it back is a re-save.
          </div>
          {!readOnly && (
            <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
              <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} />
              <button className="tb-btn tb-btn-primary" disabled={busy}
                onClick={() => inputRef.current?.click()}>
                {busy ? 'Working...' : logoUrl ? 'Replace logo' : 'Upload logo'}
              </button>
              {logoUrl && <button className="tb-btn" disabled={busy} onClick={onRemove}>Remove</button>}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ModulesPane({ modules, readOnly, onChange }) {
  return (
    <div className="ss-modules">
      {SITE_MODULES.map(m => {
        const on = modules[m.key] !== false;
        return (
          <div key={m.key} className={'ua-ga-row' + (on ? ' on' : '')}>
            <div style={{ minWidth: 0 }}>
              <div className="ua-ga-title">{m.label}</div>
              <div className="ua-ga-note">{m.note}</div>
            </div>
            <label className="ua-active">
              <input type="checkbox" checked={on} disabled={readOnly}
                onChange={e => onChange({ ...modules, [m.key]: e.target.checked ? true : false })} />
              {on ? 'Shown' : 'Hidden'}
            </label>
          </div>
        );
      })}
      <div className="ss-note">
        Counting and Settings are not on this list. Counting is what the app is for, and
        switching Settings off would take away the screen that switches it back on.
      </div>
    </div>
  );
}

function CountingPane({ rules, setRules, readOnly, saving, onSave }) {
  return (
    <>
      <div className="ua-fields">
        <Field label="Variance alert threshold"
          hint="A counted line this far from what was expected is flagged for review. Per cent.">
          <input className="fld" type="number" min="0" max="100" disabled={readOnly}
            value={rules.variance_alert_threshold_pct}
            onChange={e => setRules(r => ({ ...r, variance_alert_threshold_pct: e.target.value }))} />
        </Field>
        <Field label="Warn about counts not started"
          hint="Days before a cycle closes to raise the locations nobody has opened yet.">
          <input className="fld" type="number" min="0" max="60" disabled={readOnly}
            value={rules.not_started_alert_days_before_close}
            onChange={e => setRules(r => ({ ...r, not_started_alert_days_before_close: e.target.value }))} />
        </Field>
      </div>
      <div className="ss-note">
        A blank quantity means nobody has counted that line; a zero means somebody looked and
        found none. These thresholds read counted lines, and never turn a blank into a zero.
      </div>
      {!readOnly && (
        <div className="ua-actions">
          <span style={{ flex: 1 }} />
          <button className="tb-btn tb-btn-primary" disabled={saving} onClick={onSave}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      )}
    </>
  );
}

// Release notes and terms are the same shape - write one, publish it, and each
// person is shown it once - so they are one component told which table it is
// working in rather than two that would drift apart.
const NOTICE = {
  release_note: {
    table: 'release_notes', hasTitle: true,
    newLabel: 'New release note',
    empty: 'No release notes yet. Publish one and everybody sees it the next time they sign in.',
    published: 'Published. Everyone in this site sees it once, at their next sign-in.',
  },
  terms: {
    table: 'site_terms', hasTitle: false,
    newLabel: 'New terms',
    empty: 'No terms yet. Publish some and people are asked to accept them before they carry on.',
    published: 'Published. People are asked to accept these before they carry on.',
  },
};

function NoticePane({ kind, rows, readOnly, profile, toast, onChanged }) {
  const meta = NOTICE[kind];
  const blank = { version: '', title: '', body: '' };
  const [draft, setDraft] = useState(blank);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  async function create(publish) {
    if (!draft.version.trim() || !draft.body.trim()) {
      toast.error('A version and some words are both needed.');
      return;
    }
    if (meta.hasTitle && !draft.title.trim()) { toast.error('Give it a title.'); return; }
    setBusy(true);
    const row = {
      version: draft.version.trim(),
      body: draft.body.trim(),
      created_by: profile?.id || null,
      published_at: publish ? new Date().toISOString() : null,
      ...(meta.hasTitle ? { title: draft.title.trim() } : {}),
    };
    const { error } = await supabase.from(meta.table).insert(row);
    setBusy(false);
    if (error) { toast.error('Could not save: ' + error.message); return; }
    toast.success(publish ? meta.published : 'Saved as a draft.');
    setDraft(blank); setEditing(false);
    onChanged();
  }

  async function publishRow(id) {
    const { error } = await supabase.from(meta.table)
      .update({ published_at: new Date().toISOString() }).eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success(meta.published);
    onChanged();
  }

  async function removeRow(id, label) {
    if (!window.confirm('Delete ' + label + '? Who has already seen it goes with it.')) return;
    const { error } = await supabase.from(meta.table).delete().eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Deleted.');
    onChanged();
  }

  return (
    <>
      {rows.length === 0 && !editing && <div className="ss-note">{meta.empty}</div>}

      <div className="ss-notices">
        {rows.map(r => (
          <div key={r.id} className={'ss-notice' + (r.published_at ? ' live' : '')}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="ua-ga-title">
                {meta.hasTitle ? r.title : 'Terms'} <span className="ua-rolepill">v{r.version}</span>
                {r.published_at
                  ? <span className="ua-here" style={{ marginLeft: 8 }}>Published</span>
                  : <span className="ua-inactive" style={{ marginLeft: 8 }}>Draft</span>}
              </div>
              <div className="ss-notice-body">{r.body}</div>
            </div>
            {!readOnly && (
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {!r.published_at && (
                  <button className="tb-btn tb-btn-primary" onClick={() => publishRow(r.id)}>Publish</button>
                )}
                <button className="tb-btn tb-btn-ghost"
                  onClick={() => removeRow(r.id, meta.hasTitle ? r.title : 'v' + r.version)}>Delete</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (editing ? (
        <div className="ua-section">
          <div className="ua-section-head">
            <span className="ua-section-title">{meta.newLabel}</span>
            <span className="ua-section-note">
              Save it as a draft to write it over time. Nothing is shown to anybody until it
              is published.
            </span>
          </div>
          <div className="ua-fields">
            <Field label="Version" hint="Anything you can tell apart later: 1.4.0, or a date.">
              <input className="fld" value={draft.version}
                onChange={e => setDraft(d => ({ ...d, version: e.target.value }))} />
            </Field>
            {meta.hasTitle && (
              <Field label="Title">
                <input className="fld" value={draft.title}
                  onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} />
              </Field>
            )}
            <Field label={meta.hasTitle ? 'What changed' : 'The terms'} span>
              <textarea className="fld" rows={meta.hasTitle ? 6 : 12} value={draft.body}
                onChange={e => setDraft(d => ({ ...d, body: e.target.value }))} />
            </Field>
          </div>
          <div className="ua-actions">
            <button className="tb-btn tb-btn-ghost" onClick={() => { setDraft(blank); setEditing(false); }}>Cancel</button>
            <span style={{ flex: 1 }} />
            <button className="tb-btn" disabled={busy} onClick={() => create(false)}>Save as draft</button>
            <button className="tb-btn tb-btn-primary" disabled={busy} onClick={() => create(true)}>Publish now</button>
          </div>
        </div>
      ) : (
        <div className="ua-actions">
          <span style={{ flex: 1 }} />
          <button className="tb-btn tb-btn-primary" onClick={() => setEditing(true)}>+ {meta.newLabel}</button>
        </div>
      ))}
    </>
  );
}
